import { useState, useCallback, useMemo, type FormEvent } from 'react'
import { api, type ApiError } from '../lib/api'
import { useAuthStore } from '../lib/auth-store'
import { navigate } from '../lib/router'
import { DoCaptcha, hasDoCaptchaKey } from '../components/DoCaptcha'
import { AuthLayout } from '../components/auth/AuthLayout'
import { FloatingInput } from '../components/ui/input'
import { Button, ButtonLink } from '../components/ui/button'
import { StatusBox } from '../components/ui/badge'

export function SignupPage() {
  const setSession = useAuthStore((s) => s.setSession)
  // Detect ?token=... — when present, this is a join-via-invite flow.
  // The page shape stays the same; we just hide the Workspace field and
  // forward the token to the server, which validates + joins.
  const inviteToken = useMemo(() => {
    if (typeof window === 'undefined') return null
    return new URLSearchParams(window.location.search).get('token')
  }, [])
  const isInvite = Boolean(inviteToken)

  const [name, setName] = useState('')
  const [workspaceName, setWorkspaceName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [captchaToken, setCaptchaToken] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const onCaptchaVerify = useCallback((token: string) => setCaptchaToken(token), [])

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    if (hasDoCaptchaKey() && !captchaToken) {
      setError('Please complete the CAPTCHA verification.')
      return
    }
    setError(null)
    setSubmitting(true)
    try {
      const auth = await api.signup({
        name,
        email,
        password,
        ...(isInvite ? { inviteToken: inviteToken! } : { workspaceName }),
        captchaToken: captchaToken ?? undefined,
      })
      setSession(auth)
      navigate('/dashboard')
    } catch (err) {
      setError((err as ApiError).title ?? 'Signup failed')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <AuthLayout
      title={isInvite ? 'Accept your invite' : 'Create your account'}
      subtitle={isInvite ? "You'll join the workspace as soon as you finish." : 'Start building in minutes.'}
      panel={{ line1: 'Start building in minutes.', line2: 'Your workspace, your API key, your servers.' }}
      footer={
        <p className="text-center">
          Already have an account?{' '}
          <ButtonLink
            variant="link"
            size="sm"
            className="h-auto px-0 text-sm"
            href="/login"
            onClick={(e) => { e.preventDefault(); navigate('/login') }}
          >
            Log in
          </ButtonLink>
        </p>
      }
    >
      <form className="space-y-4" onSubmit={onSubmit}>
        {isInvite ? (
          <FloatingInput
            label="Your name"
            type="text"
            size="auth"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoComplete="name"
          />
        ) : (
          <div className="grid gap-4 sm:grid-cols-2">
            <FloatingInput
              label="Your name"
              type="text"
              size="auth"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoComplete="name"
            />
            <FloatingInput
              label="Workspace"
              type="text"
              size="auth"
              required
              value={workspaceName}
              onChange={(e) => setWorkspaceName(e.target.value)}
            />
          </div>
        )}

        <FloatingInput
          label="Email"
          type="email"
          size="auth"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete="email"
        />
        <FloatingInput
          label="Password"
          type="password"
          size="auth"
          required
          minLength={8}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="new-password"
          hint="Min. 8 characters"
        />

        <DoCaptcha onVerify={onCaptchaVerify} />

        {error && <StatusBox tone="destructive">{error}</StatusBox>}

        <Button type="submit" size="auth" disabled={submitting}>
          {submitting ? 'Creating account…' : 'Create account'}
        </Button>
      </form>
    </AuthLayout>
  )
}
